<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.schools', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.schools', 1) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.school_type')" for="title" />
                            <FormInput
                                required
                                id="title"
                                v-model.trim="form.school.title"
                                name="title"
                                type="text"
                                class="mt-1 w-full"
                                max="60"
                                list="school_types"
                                autocomplete="off"
                            />
                            <datalist id="school_types">
                                <option value="Colegio"></option>
                                <option value="Instituto"></option>
                                <option value="CEIP"></option>
                            </datalist>
                            <FormError :value="form.errors['school.title']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-8">
                        <label for="name" class="block text-sm font-medium text-gray-700">
                            {{ $t('messages.name') }}
                        </label>
                        <div class="mt-1 flex rounded-md shadow-sm">
                            <span
                                class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                            >
                                {{ form.school.title }}
                            </span>
                            <input
                                type="text"
                                v-model.trim="form.school.name"
                                name="company-website"
                                id="company-website"
                                class="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300"
                                placeholder="www.example.com"
                            />
                        </div>
                        <FormError :value="form.errors['school.name']" class="mt-2" />
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.school_code')" for="school_code" />
                            <FormInput
                                id="school_school_code"
                                v-model.trim="form.school.school_code"
                                name="school_code"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.languages', 1)" for="language" />
                            <FormSelect
                                id="language"
                                v-model="form.school.locale"
                                :options="options.languages"
                                optgroups
                                class="mt-1 w-full"
                            />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <label v-for="stage in options.stages" class="mr-1" :key="stage.value">
                                <input
                                    v-model="form.school.stages"
                                    name="stages"
                                    :value="stage.value"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                {{ stage.text }}
                            </label>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <hr class="mt-5" />

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="
                                    $t('messages.start_of_X_trimester', {
                                        number: 1,
                                    })
                                "
                                for="first_trimester_start_date"
                            />
                            <FormInput
                                required
                                id="first_trimester_start_date"
                                v-model="form.schoolYear.first_trimester_start_date"
                                name="first_trimester_start_date"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors['schoolYear.first_trimester_start_date']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="
                                    $t('messages.end_of_X_trimester', {
                                        number: 1,
                                    })
                                "
                                for="first_trimester_end_date"
                            />
                            <FormInput
                                required
                                id="first_trimester_end_date"
                                v-model="form.schoolYear.first_trimester_end_date"
                                name="first_trimester_end_date"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors['schoolYear.first_trimester_end_date']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="
                                    $t('messages.start_of_X_trimester', {
                                        number: 2,
                                    })
                                "
                                for="second_trimester_start_date"
                            />
                            <FormInput
                                required
                                id="second_trimester_start_date"
                                v-model="form.schoolYear.second_trimester_start_date"
                                name="second_trimester_start_date"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors['schoolYear.second_trimester_start_date']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="
                                    $t('messages.end_of_X_trimester', {
                                        number: 2,
                                    })
                                "
                                for="second_trimester_end_date"
                            />
                            <FormInput
                                required
                                id="second_trimester_end_date"
                                v-model="form.schoolYear.second_trimester_end_date"
                                name="second_trimester_end_date"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors['schoolYear.second_trimester_end_date']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="
                                    $t('messages.start_of_X_trimester', {
                                        number: 3,
                                    })
                                "
                                for="third_trimester_start_date"
                            />
                            <FormInput
                                required
                                id="third_trimester_start_date"
                                v-model="form.schoolYear.third_trimester_start_date"
                                name="third_trimester_start_date"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors['schoolYear.third_trimester_start_date']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel
                                :value="
                                    $t('messages.end_of_X_trimester', {
                                        number: 3,
                                    })
                                "
                                for="third_trimester_end_date"
                            />
                            <FormInput
                                required
                                id="third_trimester_end_date"
                                v-model="form.schoolYear.third_trimester_end_date"
                                name="third_trimester_end_date"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors['schoolYear.third_trimester_end_date']" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <hr class="mt-5" />

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.name') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.first_name')" for="first_name" />
                            <FormInput
                                required
                                id="first_name"
                                v-model.trim="form.teacher.first_name"
                                name="first_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.first_name']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.last_name')" for="last_name" />
                            <FormInput
                                required
                                id="last_name"
                                v-model.trim="form.teacher.last_name"
                                name="last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.last_name']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.second_last_name')" for="second_last_name" />
                            <FormInput
                                id="second_last_name"
                                v-model.trim="form.teacher.second_last_name"
                                name="second_last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.second_last_name']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.gender')" for="gender" />
                            <FormSelect
                                required
                                id="gender"
                                v-model="form.teacher.gender"
                                :options="options.genders"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.gender']" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.settings') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.preferred_language')" for="preferred_language" />
                            <FormSelect
                                id="preferred_language"
                                v-model="form.teacher.preferred_language"
                                :options="options.preferred_languages"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.preferred_language']" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.contact_info') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.email')" for="email" />
                            <FormInput
                                required
                                id="email"
                                v-model="form.teacher.email"
                                name="email"
                                type="email"
                                max="191"
                                autocomplete="off"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.email']" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.password') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.password')" for="password" />
                            <FormInput
                                required
                                id="password"
                                v-model="form.teacher.password"
                                name="password"
                                autocomplete="new-password"
                                type="password"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.password']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.password_repeat')" for="password_confirmation" />
                            <FormInput
                                required
                                id="password_confirmation"
                                v-model="form.teacher.password_confirmation"
                                name="password_confirmation"
                                autocomplete="new-password"
                                type="password"
                                max="191"
                                class="mt-1 w-full"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <SeeMore>
                <template v-slot:content>
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.mother_tongue')" for="mother_tongue" />
                            <FormSelect
                                id="mother_tongue"
                                v-model="form.teacher.mother_tongue"
                                :options="options.languages"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.mother_tongue']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nationality')" for="nationality" />
                            <FormSelect
                                id="nationality"
                                v-model="form.teacher.nationality"
                                :options="options.countries"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.nationality']" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.country_of_origin')" for="country_of_origin" />
                            <FormSelect
                                id="country_of_origin"
                                v-model="form.teacher.country_of_origin"
                                :options="options.countries"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors['teacher.country_of_origin']" class="mt-2" />
                        </div>
                    </div>
                </template>
            </SeeMore>

            <div class="flex justify-end mt-5 space-x-2">
                <div>
                    <FormCheckbox v-model="form.terms_of_service">
                        <a :href="meta.links.terms_of_service" target="_blank" class="text-indigo-600">
                            {{ $t('messages.read_and_accept_terms_of_service') }}
                        </a>
                        <FormError :value="form.errors.terms_of_service" class="mt-2" />
                    </FormCheckbox>
                </div>

                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import RegisterLayout from '@/Shared/RegisterLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormCheckbox from '@/components/Forms/FormCheckbox.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import SeeMore from '../../../components/SeeMore.vue';

export default {
    components: {
        SeeMore,
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormCheckbox,
    },
    layout: RegisterLayout,
    name: 'Edit',
    props: ['meta', 'options', 'school', 'schoolYear'],
    data() {
        return {
            form: this.$inertia.form({
                terms_of_service: false,
                school: {
                    title: this.title,
                    name: this.school.name,
                    school_code: this.school.school_code,
                    locale: this.school.locale,
                    stages: this.school.stages,
                },
                schoolYear: {
                    first_trimester_start_date: this.schoolYear.first_trimester_start_date,
                    first_trimester_end_date: this.schoolYear.first_trimester_end_date,
                    second_trimester_start_date: this.schoolYear.second_trimester_start_date,
                    second_trimester_end_date: this.schoolYear.second_trimester_end_date,
                    third_trimester_start_date: this.schoolYear.third_trimester_start_date,
                    third_trimester_end_date: this.schoolYear.third_trimester_end_date,
                },
                teacher: {
                    first_name: '',
                    name: '',
                    last_name: '',
                    second_last_name: '',
                    date_of_birth: '',
                    email: '',
                    gender: '',
                    mother_tongue: this.school.locale,
                    nationality: 'ES',
                    country_of_origin: 'ES',
                    password: '',
                    password_confirmation: '',
                    preferred_language: this.school.locale,
                },
            }),
        };
    },
    methods: {
        submit() {
            this.form.patch(this.meta.links.update);
        },
    },
};
</script>
